import type { Session } from "../Session.model";
import { useQuery } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { User } from "@/entities/User";
import { getIdentitySessionQuery } from "../Session.queries";
import { useToken } from "./useToken";
import { SESSION_GC_TIME, SESSION_STALE_TIME } from "./Session.constants";
import { useEffect } from "react";
import { log } from "@/utils";

export const useSession = ({
    enabled = true,
}: {
    enabled?: boolean;
} = {}): Session => {
    const auth0Session = useAuth0();

    const token = useToken();

    const identity = useQuery<User | null>({
        ...getIdentitySessionQuery(token.data),
        enabled: enabled && !!token.data,
        staleTime: SESSION_STALE_TIME,
        gcTime: SESSION_GC_TIME,
    });

    const auth0Loading =
        !auth0Session.isAuthenticated && auth0Session.isLoading;
    const tokenLoading = !token.data && token.isLoading;
    const identityLoading = !identity.data && identity.isLoading;

    const identityError = !identity.data && identity.isError;
    const auth0Error = !auth0Session.user && !!auth0Session.error;
    const tokenError = !token.data && token.isError;

    const isHumError =
        auth0Session.isAuthenticated && !!token.data && identityError;

    useEffect(() => {
        if (isHumError) {
            log.error("Auth0 session is authenticated but identity failed", {
                auth0Id: auth0Session?.user?.sub,
                email: auth0Session?.user?.email,
                name: auth0Session?.user?.name,
                identityError: identity.error,
            });
        }
    }, [isHumError]);

    return {
        isLoading:
            auth0Loading ||
            (auth0Session.isAuthenticated && (tokenLoading || identityLoading)),
        isAuthenticated: auth0Session.isAuthenticated && !!identity.data,
        isError: auth0Error || tokenError || identityError,
        isHumError,
        auth0: auth0Session?.user || null,
        token,
        user: identity.data,
    };
};
